import React, { useEffect, useMemo, useState } from "react";
import compose from "recompose/compose";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import OrganizerCommonHeader from "../OrganizerCommonHeader";
import Breadcrumbs from "./../../../../common/Breadcrumbs";

import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormControl,
  NativeSelect,
  TableContainer,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import GroupIcon from "@material-ui/icons/Group";

import {
  createTeamApi,
  getRunnersWithoutTeam,
  getTeamList,
  updateTeamApi,
} from "../../../../../apis/eventService";

import ApplicationUtil from "../../../../../utils/ApplicationUtil";
import Snackbar from "../../../../common/Snackbar";
import SubmitCancelBtn from "../../../../common/SubmitCancelBtn";

const styles = (theme) => ({
  page: {
    width: "100%",
    boxSizing: "border-box",
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),

    height: "calc(100vh - 140px)",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 160px)",
    },
  },

  pageInner: {
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),

    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },

  layout: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(3),
    width: "100%",
    height: "100%",
    boxSizing: "border-box",
    minWidth: 0,

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
    },
  },

  // LEFT PANEL
  leftPanel: {
    width: 330,
    flex: "0 0 330px",
    boxSizing: "border-box",
    padding: theme.spacing(2),
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    height: "100%",
    overflow: "hidden",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flex: "0 0 auto",
      height: "auto",
    },
  },

  fieldLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#6c757d",
    marginBottom: theme.spacing(0.5),
  },

  textInput: {
    width: "100%",
    boxSizing: "border-box",
    border: "none",
    borderBottom: "1px solid #bfc0c0",
    outline: "none",
    fontSize: 16,
    padding: "8px 2px",
    marginBottom: theme.spacing(2),
  },

  selectWrap: {
    width: "100%",
    marginBottom: theme.spacing(2),
    "& select": {
      width: "100%",
      padding: "8px 2px",
      fontSize: 16,
      color: "#111827",
      border: "none",
      borderBottom: "1px solid #bfc0c0",
      outline: "none",
      background: "transparent",
    },
  },

  helpText: {
    fontSize: 13,
    color: "#6b7280",
    marginTop: theme.spacing(1),
    lineHeight: 1.35,
  },

  // RIGHT PANEL
  rightPanel: {
    flex: "1 1 auto",
    minWidth: 0,
    boxSizing: "border-box",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    background: "#fff",
    padding: theme.spacing(2),
    height: "100%",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  rightHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
    flex: "0 0 auto",
  },

  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
  },

  search: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #bfc0c0",
    borderRadius: 6,
    height: 36,
    width: 320,
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",

    "& > input": {
      flex: 1,
      minWidth: 0,
      height: 30,
      outline: "none",
      border: "none",
      fontSize: 16,
      paddingLeft: 10,
      borderRight: "2px solid #bfc0c0",
      boxSizing: "border-box",
    },

    "& .search__icon": {
      padding: 6,
      cursor: "pointer",
      flexShrink: 0,
    },
  },

  tableWrap: {
    flex: "1 1 auto",
    overflowY: "auto",
    borderRadius: 8,
    border: "1px solid #e5e7eb",
  },

  tableHead: {
    backgroundColor: "#ced4da",
    "& th": {
      color: "#6c757d",
      fontFamily: "Rubik, sans-serif",
      fontSize: "16px",
      fontWeight: "bolder",
      textAlign: "center",
      borderRight: "1px solid #adb5bd",
    },
  },

  tableRow: {
    "& td": {
      fontWeight: "bold",
      fontFamily: "Rubik, sans-serif",
      borderBottom: "1px solid #e5e7eb",
    },

    "&.MuiTableRow-hover:hover": {
      backgroundColor: "#f8f9fa",
    },

    // selected row
    "&.Mui-selected": {
      backgroundColor: "#e9ecef",
    },

    "&.Mui-selected:hover": {
      backgroundColor: "#e9ecef",
    },
  },

  emptyState: {
    flex: "1 1 auto",
    minHeight: 220,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(1),
    color: "#6b7280",
    borderRadius: 8,
    border: "1px dashed #d1d5db",
    background: "#fafafa",
    padding: theme.spacing(3),
  },
});

function CreateTeam(props) {
  const { classes, history } = props;

  const params = new URLSearchParams(props.location.search);
  const teamIdParam = params.get("teamId");
  const isEdit = Boolean(teamIdParam);
  const teamId = teamIdParam ? Number(teamIdParam) : null;

  const eventId = useMemo(() => ApplicationUtil.getSupportsEventID(), []);

  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [runners, setRunners] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [teamName, setTeamName] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [teamLeadRunnerId, setTeamLeadRunnerId] = useState("");
  const [originalTeamName, setOriginalTeamName] = useState("");
  const [originalSelectedIds, setOriginalSelectedIds] = useState([]);

  const selectedRunnerObjects = useMemo(() => {
    if (!runners?.length || !selectedIds?.length) return [];
    const set = new Set(selectedIds);
    return runners.filter((r) => set.has(r?.id));
  }, [runners, selectedIds]);

  useEffect(() => {
    if (!isEdit || !teamId) return;

    setInitialLoading(true);

    getTeamList({
      eventId: Number(eventId),
      teamId: Number(teamId),
      leaderboard: false,
    })
      .then((res) => {
        const data = res?.data || {};

        const nameFromApi = (data?.teamName || "").trim();
        setTeamName(nameFromApi);
        setOriginalTeamName(nameFromApi);
        setTeamLeadRunnerId(
          data?.teamLeaderId ? String(data.teamLeaderId) : "",
        );
      })
      .catch(() => {
        Snackbar.show({
          variant: "error",
          isAutoHide: true,
          preventDuplicate: true,
          message: "Failed to load team details.",
        });
      })
      .finally(() => setInitialLoading(false));
  }, [isEdit, teamId, eventId]);

  const fetchRunners = (text = "") => {
    setLoading(true);

    const includeTeamId = isEdit && teamId ? Number(teamId) : undefined;

    getRunnersWithoutTeam(eventId, text, includeTeamId)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setRunners(list);

        if (isEdit && teamId) {
          const ids = list
            .filter((r) => Number(r?.teamId) === Number(teamId))
            .map((r) => r?.id)
            .filter((id) => typeof id === "number");

          setSelectedIds(ids);
          setOriginalSelectedIds(ids);

          if (teamLeadRunnerId) {
            const leadId = Number(teamLeadRunnerId);
            if (!ids.includes(leadId)) setTeamLeadRunnerId("");
          }
        }
      })
      .catch(() => setRunners([]))
      .finally(() => setLoading(false));
  };

  // initial runners fetch
  useEffect(() => {
    fetchRunners("");
  }, [isEdit, teamId]);

  const onSearchSubmit = () => fetchRunners(searchText || "");

  const toggleSelectAllVisible = (checked) => {
    const visibleIds = (runners || [])
      .map((r) => r?.id)
      .filter((id) => typeof id === "number");

    if (!checked) {
      const visibleSet = new Set(visibleIds);
      setSelectedIds((prev) => prev.filter((id) => !visibleSet.has(id)));
      return;
    }

    setSelectedIds((prev) => {
      const set = new Set(prev);
      visibleIds.forEach((id) => set.add(id));
      return Array.from(set);
    });
  };

  const toggleRow = (runnerId, checked) => {
    if (typeof runnerId !== "number") return;

    setSelectedIds((prev) => {
      const set = new Set(prev);
      if (checked) set.add(runnerId);
      else set.delete(runnerId);
      return Array.from(set);
    });
  };

  const allVisibleSelected = useMemo(() => {
    const visibleIds = (runners || [])
      .map((r) => r?.id)
      .filter((id) => typeof id === "number");
    if (!visibleIds.length) return false;

    const set = new Set(selectedIds);
    return visibleIds.every((id) => set.has(id));
  }, [runners, selectedIds]);

  const someVisibleSelected = useMemo(() => {
    const visibleIds = (runners || [])
      .map((r) => r?.id)
      .filter((id) => typeof id === "number");
    if (!visibleIds.length) return false;

    const set = new Set(selectedIds);
    return visibleIds.some((id) => set.has(id)) && !allVisibleSelected;
  }, [runners, selectedIds, allVisibleSelected]);

  useEffect(() => {
    if (initialLoading) return;
    if (!teamLeadRunnerId) return;

    const leadIdNum = Number(teamLeadRunnerId);
    if (!selectedIds.includes(leadIdNum)) setTeamLeadRunnerId("");
  }, [selectedIds, teamLeadRunnerId, initialLoading]);

  const submitTeam = () => {
    const trimmedName = (teamName || "").trim();
    const rawLeadId = teamLeadRunnerId;

    const leadId =
      rawLeadId !== "" && rawLeadId !== null && rawLeadId !== undefined
        ? Number(rawLeadId)
        : null;

    if (!trimmedName) {
      Snackbar.show({
        variant: "error",
        isAutoHide: true,
        preventDuplicate: true,
        message: "Please enter Team Name.",
      });
      return;
    }

    if (!selectedIds?.length) {
      Snackbar.show({
        variant: "error",
        isAutoHide: true,
        preventDuplicate: true,
        message: "Please select at least one participant.",
      });
      return;
    }

    // Base payload (common fields)
    const basePayload = {
      eventId: Number(eventId),
      teamLeadRunnerId: leadId,
      runnerId: selectedIds,
    };

    setLoading(true);

    if (isEdit && teamId) {
      const nameChanged = trimmedName !== (originalTeamName || "").trim();

      const prevSet = new Set(originalSelectedIds || []);
      const nextSet = new Set(selectedIds || []);

      const addRunnerIds = Array.from(nextSet).filter((id) => !prevSet.has(id));
      const removeRunnerIds = Array.from(prevSet).filter(
        (id) => !nextSet.has(id),
      );

      const updatePayload = {
        teamId: Number(teamId),
        teamLeadRunnerId: leadId,
        addRunnerIds,
        removeRunnerIds,
        ...(nameChanged ? { teamName: trimmedName } : {}),
      };

      setLoading(true);

      updateTeamApi(updatePayload)
        .then((res) => {
          Snackbar.show({
            variant: "success",
            isAutoHide: true,
            preventDuplicate: true,
            message: res?.data?.verbose || "Team updated successfully.",
          });
          history.goBack();
        })
        .catch((err) => {
          Snackbar.show({
            variant: "error",
            isAutoHide: true,
            preventDuplicate: true,
            message:
              err?.response?.data?.error?.verbose ||
              err?.response?.data?.error?.message ||
              "Failed to update team.",
          });
        })
        .finally(() => setLoading(false));

      return;
    }

    // Create team flow
    const createPayload = {
      ...basePayload,
      teamName: trimmedName,
    };

    createTeamApi(createPayload)
      .then((res) => {
        Snackbar.show({
          variant: "success",
          isAutoHide: true,
          preventDuplicate: true,
          message: res?.data?.verbose || "Team created successfully.",
        });
        history.goBack();
      })
      .catch((err) => {
        Snackbar.show({
          variant: "error",
          isAutoHide: true,
          preventDuplicate: true,
          message:
            err?.response?.data?.error?.verbose ||
            err?.response?.data?.error?.message ||
            "Failed to create team.",
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className="organizer">
      <OrganizerCommonHeader />

      <div className="organizer__mainBody">
        <Breadcrumbs />

        <div className={classes.page}>
          <div className={classes.pageInner}>
            <div className={classes.layout}>
              {/* LEFT PANEL */}
              <div className={classes.leftPanel}>
                <Typography className={classes.fieldLabel}>
                  Team Name
                </Typography>
                <input
                  className={classes.textInput}
                  placeholder="Enter team name"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />

                <Typography className={classes.fieldLabel}>
                  Select Team Lead
                </Typography>
                <FormControl className={classes.selectWrap}>
                  <NativeSelect
                    value={teamLeadRunnerId}
                    onChange={(e) => setTeamLeadRunnerId(e.target.value)}
                  >
                    <option value="None">-- None --</option>
                    {selectedRunnerObjects.map((r) => (
                      <option key={r?.id} value={r?.id}>
                        {r?.fullName ||
                          `${r?.firstName || ""} ${r?.lastName || ""}`.trim() ||
                          "-"}
                      </option>
                    ))}
                  </NativeSelect>
                </FormControl>

                <SubmitCancelBtn
                  style={{ borderRadius: "10px", width: "160px" }}
                  label={
                    loading || initialLoading
                      ? "Please wait..."
                      : isEdit
                        ? "Update Team"
                        : "Create Team"
                  }
                  type="submit"
                  onClick={submitTeam}
                  isDisabled={
                    loading ||
                    initialLoading ||
                    !(teamName || "").trim() ||
                    selectedIds.length === 0
                  }
                />

                <div className={classes.helpText}>
                  <div>
                    <b>Selected:</b> {selectedIds.length}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    Select participants from the table, then choose a Team Lead
                    from selected participants.
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL */}
              <div className={classes.rightPanel}>
                <div className={classes.rightHeader}>
                  <Typography className={classes.title}>
                    {isEdit
                      ? "Edit Team"
                      : "Please select participants to create Team"}
                  </Typography>

                  <form
                    className={classes.search}
                    onSubmit={(e) => {
                      e.preventDefault();
                      onSearchSubmit();
                    }}
                  >
                    <input
                      type="search"
                      placeholder="Search..."
                      value={searchText}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSearchText(val);
                        if (!val) fetchRunners("");
                      }}
                    />
                    <SearchIcon
                      className="search__icon"
                      onClick={onSearchSubmit}
                    />
                  </form>
                </div>

                {(loading || initialLoading) && runners?.length === 0 ? (
                  <div className={classes.emptyState}>
                    <GroupIcon style={{ width: 64, height: 64 }} />
                    <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                      Loading participants...
                    </Typography>
                  </div>
                ) : runners?.length > 0 ? (
                  <TableContainer className={classes.tableWrap}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow className={classes.tableHead}>
                          <TableCell style={{ width: 70 }}>
                            <Checkbox
                              checked={allVisibleSelected}
                              indeterminate={someVisibleSelected}
                              onChange={(e) =>
                                toggleSelectAllVisible(e.target.checked)
                              }
                              color="primary"
                            />
                          </TableCell>

                          <TableCell
                            align="left"
                            style={{ paddingLeft: 15, textAlign: "left" }}
                          >
                            BIB
                          </TableCell>

                          <TableCell
                            align="left"
                            style={{ paddingLeft: 15, textAlign: "left" }}
                          >
                            Name
                          </TableCell>

                          <TableCell
                            align="left"
                            style={{ paddingLeft: 15, textAlign: "left" }}
                          >
                            Email
                          </TableCell>

                          <TableCell
                            align="left"
                            style={{ paddingLeft: 15, textAlign: "left" }}
                          >
                            Contact
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {runners.map((row) => {
                          const id = row?.id;
                          const isSelected = selectedIds.includes(id);

                          return (
                            <TableRow
                              key={id}
                              className={classes.tableRow}
                              selected={isSelected}
                              hover
                            >
                              <TableCell align="center">
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) =>
                                    toggleRow(id, e.target.checked)
                                  }
                                  color="primary"
                                />
                              </TableCell>

                              <TableCell align="left">
                                <p style={{ margin: 0, paddingLeft: 10 }}>
                                  {row?.bib || "-"}
                                </p>
                              </TableCell>

                              <TableCell align="left">
                                <p style={{ margin: 0, paddingLeft: 10 }}>
                                  {row?.fullName ||
                                    `${row?.firstName || ""} ${
                                      row?.lastName || ""
                                    }`.trim() ||
                                    "-"}
                                </p>
                              </TableCell>

                              <TableCell align="left">
                                <p style={{ margin: 0, paddingLeft: 10 }}>
                                  {row?.email || "-"}
                                </p>
                              </TableCell>

                              <TableCell align="left">
                                <p style={{ margin: 0, paddingLeft: 10 }}>
                                  {row?.contactNumber || "-"}
                                </p>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <div className={classes.emptyState}>
                    <GroupIcon style={{ width: 64, height: 64 }} />
                    <Typography style={{ fontSize: 16, fontWeight: 700 }}>
                      No participants available
                    </Typography>
                    <Typography style={{ textAlign: "center", maxWidth: 520 }}>
                      All participants are already in a team, or no matching
                      participants found for your search.
                    </Typography>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default compose(
  withStyles(styles, { withTheme: true }),
  withRouter,
)(CreateTeam);
